import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, setAuth, clearAuth } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { TIDS } from "@/constants/testIds";
import { ShieldCheck, Loader2 } from "lucide-react";

export default function AdminLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [needOtp, setNeedOtp] = useState(false);
  const [loading, setLoading] = useState(false);

  // Clear any stale token from a previous role (e.g. rider) so it can't cause 401/403 on admin routes
  useEffect(() => { clearAuth(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password, otp: otp || undefined });
      if (data.mfa_required) { setNeedOtp(true); toast.info("Enter your 6-digit MFA code"); return; }
      setAuth(data.token, data.user);
      toast.success(`Welcome back, ${data.user.name}`);
      nav("/admin");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429) toast.error(detail || "Account locked. Try again in 15 minutes.", { duration: 8000 });
      else if (status === 401) toast.error("Wrong email or password");
      else if (status === 403) toast.error(`Access denied (403): ${detail || "check your account status"}`);
      else if (!e?.response) toast.error("Network / proxy error — check your connection or VPN");
      else toast.error(detail || `Login failed (${status})`);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#090D14] grid place-items-center text-white p-4">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-amber-500/10 blur-[100px] rounded-full" />
      </div>
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-10 justify-center">
          <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 grid place-items-center text-[#090D14] font-black text-xl font-display">A</div>
          <div className="leading-tight">
            <div className="font-display font-extrabold tracking-tight text-white text-xl">Apex Taxi</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-amber-400/80 font-mono">Dispatch Console</div>
          </div>
        </Link>

        <form onSubmit={submit} className="glass-card rounded-2xl p-8 gold-border fade-up">
          <div className="text-caption mb-2 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Secure Admin</div>
          <h1 className="font-display text-2xl font-bold">Sign in to dashboard</h1>
          <p className="text-sm text-slate-400 mt-1">Restricted area — bookings, drivers, and payments.</p>

          <div className="mt-6 space-y-4">
            <div>
              <Label className="text-xs text-slate-400 uppercase tracking-wider">Email</Label>
              <Input data-testid={TIDS.admin.email} value={email} onChange={(e) => setEmail(e.target.value)} type="email" required className="bg-black/30 border-white/10 h-11 mt-2" placeholder="you@apextaxi.co.nz" />
            </div>
            <div>
              <Label className="text-xs text-slate-400 uppercase tracking-wider">Password</Label>
              <Input data-testid={TIDS.admin.password} value={password} onChange={(e) => setPassword(e.target.value)} type="password" required className="bg-black/30 border-white/10 h-11 mt-2" placeholder="••••••••" />
            </div>
            {needOtp && (
              <div>
                <Label className="text-xs text-slate-400 uppercase tracking-wider">MFA code</Label>
                <Input data-testid="admin-login-otp" value={otp} onChange={(e) => setOtp(e.target.value)} maxLength={6} className="bg-black/30 border-white/10 h-11 mt-2 font-mono text-center text-lg tracking-widest" placeholder="123456" />
              </div>
            )}
            <Button data-testid={TIDS.admin.submit} type="submit" disabled={loading} className="w-full btn-gold rounded-lg h-11">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Sign in
            </Button>
            <div className="text-center pt-2">
              <Link to="/admin/forgot-password" data-testid="forgot-password-link" className="text-xs text-slate-400 hover:text-amber-400 transition-colors">Forgot your password?</Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
